import React, { useEffect, useState } from "react";
import { OpenWindowConfig } from "../windowManager/WindowManagerContext";
import { I } from "../redesign/kvpIcons";
import { MailTemplateSection } from "./MailTemplateSection";
import { MailSignatureSection } from "./MailSignatureSection";
import { MailRegelSection } from "./MailRegelSection";
import { MailAnsichtSection } from "./MailAnsichtSection";
import { MailPostfachSection } from "./MailPostfachSection";
import "./mailSettings.css";

export type MailSettingsTab = "templates" | "signatures" | "rules" | "ansicht" | "postfaecher";

/**
 * Reiter-Wunsch an ein BEREITS OFFENES Einstellungsfenster.
 *
 * `openWindow` legt ein Fenster mit schon vergebener Id nicht neu an, sondern
 * holt das bestehende nur nach vorn (WindowManagerContext, Reducer „open") —
 * `render` und damit `initialTab` laufen kein zweites Mal. Ohne dieses Signal
 * landete „Signatur erstellen…" aus dem Verfassen-Fenster also auf dem Reiter,
 * der zufällig zuletzt offen war.
 */
type ReiterHoerer = (tab: MailSettingsTab) => void;
const reiterHoerer = new Set<ReiterHoerer>();

function wuenscheReiter(tab: MailSettingsTab): void {
    for (const h of Array.from(reiterHoerer)) {
        try {
            h(tab);
        } catch {
            /* ein defekter Hörer darf die anderen nicht mitreißen */
        }
    }
}

/**
 * Inhalt des Mail-Einstellungs-Fensters. Reiter „Vorlagen" | „Signaturen" |
 * „Regeln" mit je einer CRUD-Section, dazu „Darstellung" (T-29: Unterhaltungen
 * oder einzelne Mails — dieselbe Wahl wie der Schalter in der Mail-App) und
 * „Postfächer" (T-249: welche Postfächer in der Glocke zählen; T-289:
 * Standard-Absender).
 *
 * „Regeln" (Cluster REGELN, H5) zeigt bewusst NUR die eigenen Regeln: Namen und
 * Bedingungen enthalten Absenderadressen und Vorgangsbezüge. Regeln an einem
 * Vorgang bleiben zusätzlich über den Vorgang selbst erreichbar.
 *
 * Fluent-frei: eigene Reiter-Leiste im Refined-Stil statt `TabList`.
 */
export const MailSettingsContent: React.FC<{ initialTab?: MailSettingsTab }> = ({
    initialTab = "templates",
}) => {
    const [tab, setTab] = useState<MailSettingsTab>(initialTab);

    // Ein erneutes Öffnen bei schon sichtbarem Fenster kommt nur hierüber an.
    useEffect(() => {
        reiterHoerer.add(setTab);
        return () => {
            reiterHoerer.delete(setTab);
        };
    }, []);
    const mkTab = (value: MailSettingsTab, label: string) => (
        <button
            type="button"
            role="tab"
            aria-selected={tab === value}
            className={`mset-tab${tab === value ? " active" : ""}`}
            onClick={() => setTab(value)}
        >
            {label}
        </button>
    );

    return (
        <div
            className="mset"
            style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                // Explizit weiß: der Fenster-Body des WindowManagers ist grau.
                background: "#fff",
            }}
        >
            <div
                role="tablist"
                className="mset-tabs"
                style={{
                    flexShrink: 0,
                    padding: "6px 16px 0",
                    borderBottom: "1px solid var(--mset-line-2)",
                    background: "#faf7fa",
                }}
            >
                {mkTab("templates", "Vorlagen")}
                {mkTab("signatures", "Signaturen")}
                {mkTab("rules", "Regeln")}
                {mkTab("ansicht", "Darstellung")}
                {mkTab("postfaecher", "Postfächer")}
            </div>
            {/* Nur DIESER Bereich scrollt — sonst bekäme das Fenster zwei Leisten. */}
            <div style={{ flex: 1, overflowY: "auto", padding: 16, minHeight: 0 }}>
                {tab === "templates" ? (
                    <MailTemplateSection />
                ) : tab === "signatures" ? (
                    <MailSignatureSection />
                ) : tab === "rules" ? (
                    <MailRegelSection quelle="eigene" />
                ) : tab === "postfaecher" ? (
                    <MailPostfachSection />
                ) : (
                    <MailAnsichtSection />
                )}
            </div>
        </div>
    );
};

/**
 * Öffnet das Mail-Einstellungs-Fenster als WindowManager-Fenster (Muster
 * `openComposeWindow`). `render` ist eine Funktion, damit der initiale Reiter
 * stabil gegen stale closures bleibt.
 *
 * Die Fenster-ID ist bewusst STABIL: Ein zweiter Klick aufs Zahnrad fokussiert
 * das bestehende Fenster, statt ein weiteres darüber zu stapeln. Dadurch wirkt
 * `initialTab` nur beim ersten Öffnen; ist das Fenster schon da, stellt
 * `wuenscheReiter` den Reiter um (siehe dort).
 */
export const openMailSettingsWindow = (
    openWindow: (config: OpenWindowConfig) => void,
    initialTab?: MailSettingsTab,
): string => {
    const id = "mail-settings";
    const W = 720;
    const H = 640;
    const x = Math.max(24, Math.round(window.innerWidth / 2 - W / 2 - 150));
    const y = Math.max(16, Math.round(window.innerHeight / 2 - H / 2 - 40));
    openWindow({
        id,
        title: "Mail-Einstellungen",
        icon: I.settings,
        initialSize: { width: W, height: H },
        initialPosition: { x, y },
        render: () => <MailSettingsContent initialTab={initialTab} />,
    });
    // Nur bei ausdrücklichem Wunsch umschalten — das Zahnrad der Mailliste ruft
    // ohne Reiter auf und soll ein offenes Fenster stehen lassen, wie es ist.
    if (initialTab) wuenscheReiter(initialTab);
    return id;
};
